'use client'

import React, { Dispatch, memo, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AssetSummary, GetAssetsResponse } from '@/lib/assets/types'
import { cn } from '@/lib/utils'
import styles from '@/components/assets-explorer/assets-explorer.module.scss'
import { getAssetKey, getDetailUrl } from '@/lib/assets/assets'
import { AssetPreview } from '@/components/assets-explorer/asset-preview'
import HiddenSEOContent from '@/components/hidden-seo-content'
import Link from 'next/link'
import { useVirtualizer } from '@tanstack/react-virtual'
import useCountGridColumns from '@/lib/hooks/useCountGridColumns'
import isEqual from 'react-fast-compare'
import { useAudioStore } from '@/lib/store/audio-store'
import { MusicBar } from '@/components/ui/music-bar'
import { FloatingActions } from '@/components/floating-actions'
import debounce from 'lodash/debounce'
import { ASSETS_GRID_SCROLLER_ID } from '@/lib/config'

export type AssetsGrid = Array<Array<AssetSummary>>
export type AssetsPages = Array<GetAssetsResponse>

export interface AssetsVirtualGridInnerProps {
  assetsGrid: AssetsGrid
  scrollElement?: Element | null
  columnCount?: number
  itemWidth?: number
  itemHeight?: number
  gap?: number
  isAudio?: boolean
  audioGridLayout?: boolean
  scrollMargin?: number
  enabled?: boolean
  setSelectedAsset?: Dispatch<SetStateAction<AssetSummary | null>>
  loadMore?: () => void
}

const AssetsVirtualGridInner = memo(
  React.forwardRef<HTMLDivElement, AssetsVirtualGridInnerProps>(
    (
      {
        assetsGrid,
        scrollElement = null,
        columnCount = 0,
        itemWidth = 0,
        itemHeight = 0,
        gap = 0,
        isAudio = false,
        audioGridLayout = false,
        scrollMargin = 0,
        enabled = true,
        setSelectedAsset = () => {},
        loadMore = () => {},
      }: AssetsVirtualGridInnerProps,
      ref
    ) => {
      const listRef = useRef<HTMLUListElement>(null)
      const [showScrollToTop, setShowScrollToTop] = useState(false)

      const rowCount = useMemo(() => assetsGrid?.length ?? 0, [assetsGrid])

      const rowVirtualizer = useVirtualizer({
        count: rowCount,
        getScrollElement: () => scrollElement,
        estimateSize: () => itemHeight,
        gap,
        useAnimationFrameWithResizeObserver: true,
        scrollMargin,
        initialOffset: scrollMargin,
        overscan: isAudio && !audioGridLayout ? 5 : 3,
        enabled,
        scrollToFn: (_offset, _options) => {
          // Do nothing
        },
      })

      const columnVirtualizer = useVirtualizer({
        horizontal: true,
        count: columnCount,
        getScrollElement: () => scrollElement,
        estimateSize: () => itemWidth,
        gap,
        useAnimationFrameWithResizeObserver: true,
        overscan: 0,
        enabled,
        scrollToFn: (_offset, _options) => {
          // Do nothing
        },
      })

      const rowVirtualItems = rowVirtualizer.getVirtualItems()

      useEffect(() => {
        const totalRows = assetsGrid.length

        console.debug(
          `Showing ${rowVirtualItems.length} of ${totalRows} rows, form index ${rowVirtualItems[0]?.index} to ${
            rowVirtualItems[rowVirtualItems.length - 1]?.index
          }`
        )
      }, [assetsGrid.length, rowVirtualItems])

      useEffect(() => {
        const [lastVirtualItem] = [...rowVirtualItems].reverse()

        if (!lastVirtualItem) {
          return
        }

        if (lastVirtualItem.index >= assetsGrid.length - 1) {
          console.debug('Last item reached')
          loadMore()
        }
      }, [assetsGrid, loadMore, rowVirtualItems])

      // Cleanup audio player on unmount
      const cleanup = useAudioStore((state) => state.cleanup)
      useEffect(() => {
        return () => {
          if (isAudio) {
            cleanup()
          }
        }
      }, [isAudio, cleanup])

      // Update showScrollToTop state based on scroll position
      useEffect(() => {
        if (!scrollElement) {
          return
        }

        const handleScroll = debounce(() => {
          const scrollTop = scrollElement.scrollTop
          setShowScrollToTop(scrollTop > 300)
        }, 500)

        scrollElement.addEventListener('scroll', handleScroll)

        return () => {
          scrollElement.removeEventListener('scroll', handleScroll)
        }
      }, [scrollElement])

      // Function to scroll to top
      const scrollToTop = useCallback(() => {
        if (!scrollElement) {
          return
        }

        scrollElement.scrollTo({
          top: 0,
          behavior: 'smooth',
        })
      }, [scrollElement])

      return (
        <div
          data-slot="assets-virtual-grid-inner"
          ref={ref}
          className="assets-virtual-grid-inner tw-relative tw-z-auto tw-w-full"
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
          }}
        >
          <ul
            ref={listRef}
            data-slot="assets-virtual-grid-inner-list"
            className={cn('assets-virtual-grid-inner-list tw-h-full', {
              [styles.assetGallery]: !isAudio || !audioGridLayout,
              [styles.audioList]: isAudio && !audioGridLayout,
            })}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) =>
              columnVirtualizer.getVirtualItems().map((virtualColumn) => {
                const startX = Math.round(virtualColumn.index * itemWidth + virtualColumn.index * gap)
                const startY = Math.round(virtualRow.index * itemHeight + virtualRow.index * gap)

                const asset = assetsGrid[virtualRow.index]?.[virtualColumn.index]

                if (!asset) {
                  return null
                }

                const assetKey = asset ? getAssetKey(asset) : ''

                return (
                  <li
                    key={`${assetKey}-${virtualColumn.index}-${virtualRow.index}`}
                    style={{
                      width: itemWidth,
                      height: itemHeight,
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      transform: `translateX(${startX}px) translateY(${startY}px)`,
                    }}
                  >
                    <>
                      {isAudio && <MusicBar key={assetKey} gridLayout={audioGridLayout} asset={asset} />}
                      {!isAudio && (
                        <AssetPreview
                          asset={asset}
                          onClick={() => {
                            setSelectedAsset(asset)
                          }}
                        />
                      )}
                      <HiddenSEOContent>
                        <Link href={getDetailUrl(asset) ?? ''}>{asset.name}</Link>
                      </HiddenSEOContent>
                    </>
                  </li>
                )
              })
            )}
          </ul>

          {/* Floating actions: Portal Downloads toggle + Scroll to top */}
          <FloatingActions showScrollToTop={showScrollToTop} onScrollToTop={scrollToTop} />
        </div>
      )
    }
  ),
  isEqual
)
AssetsVirtualGridInner.displayName = 'AssetsVirtualGridInner'

export interface AssetsVirtualGridProps {
  assetsPages: AssetsPages
  isAudio?: boolean
  audioGridLayout?: boolean
  enabled?: boolean
  loadMore?: () => void
  setSelectedAsset?: Dispatch<SetStateAction<AssetSummary | null>>
}

const AssetsVirtualGrid = ({
  assetsPages,
  isAudio = false,
  audioGridLayout = false,
  enabled = true,
  loadMore = () => {},
  setSelectedAsset = () => {},
}: AssetsVirtualGridProps) => {
  const fakeGridRef = useRef<HTMLUListElement>(null)
  const documentIsPresent = typeof document !== 'undefined'

  const scrollElement = useMemo(() => {
    if (!documentIsPresent) {
      return null
    }

    const element = document.getElementById(ASSETS_GRID_SCROLLER_ID)

    if (!element) {
      console.warn(
        `AssetsVirtualGrid: No scroll element found. Make sure that an element with id "${ASSETS_GRID_SCROLLER_ID}" exists.`
      )
      return null
    }

    return element
  }, [documentIsPresent])

  // Make sure these values are in sync with the fake-grid gap
  const gap = 8
  const itemMinWidth = 260

  const {
    count: columnCount,
    width: totalWidth,
    itemSize: itemWidth,
  } = useCountGridColumns({
    target: fakeGridRef,
    minWidth: itemMinWidth,
    gap,
  })

  // Calculate item height based on the column count and whether it's audio or not
  const itemHeight = useMemo(() => {
    if (isAudio && !audioGridLayout) {
      return 57
    }
    if (isAudio && audioGridLayout) {
      return 180
    }
    if (!columnCount) {
      return 150
    }

    // The ratio for non-audio assets is 16:9. Make sure this value matches the CSS value
    return Math.floor(itemWidth * 9) / 16
  }, [columnCount, isAudio, itemWidth, audioGridLayout])

  // Divide the grid into rows and columns in the format:
  // [[asset, asset, asset],[asset, asset, asset],[asset, asset, asset]...] or
  // [[asset],[asset],[asset],[asset]...] for audio
  const resolvedColumnCount = !audioGridLayout && isAudio ? 1 : columnCount
  const resolvedItemWidth = !audioGridLayout && isAudio ? totalWidth : itemWidth
  const resolvedGap = !audioGridLayout && isAudio ? 0 : gap

  const assetsGrid: AssetsGrid = useMemo(() => {
    if (!resolvedColumnCount) {
      return []
    }
    const assetsList = assetsPages.flatMap((page) => page.data) || []
    const totalAssets = assetsList.length
    const rows = Math.ceil(totalAssets / resolvedColumnCount)
    const grid: AssetsGrid = []
    let assetIndex = 0
    for (let row = 0; row < rows; row++) {
      for (let column = 0; column < resolvedColumnCount; column++) {
        if (assetIndex >= totalAssets) {
          break
        }
        if (!grid[row]) {
          grid[row] = []
        }
        grid[row][column] = assetsList[assetIndex]
        assetIndex++
      }
    }
    return grid
  }, [assetsPages, resolvedColumnCount])

  // Calculate the top margin for the scroll element taking account the scroll position
  const gridInnerRef = useRef<HTMLDivElement>(null)
  const rect = gridInnerRef.current?.getBoundingClientRect()
  const rectTop = rect ? rect.top : 0
  const scrollAmount = scrollElement?.scrollTop ?? 0
  const scrollMargin = scrollAmount + rectTop

  return (
    <>
      <AssetsVirtualGridInner
        ref={gridInnerRef}
        assetsGrid={assetsGrid}
        scrollElement={scrollElement}
        columnCount={resolvedColumnCount}
        itemWidth={resolvedItemWidth}
        itemHeight={itemHeight}
        isAudio={isAudio}
        audioGridLayout={audioGridLayout}
        gap={resolvedGap}
        scrollMargin={scrollMargin}
        enabled={enabled}
        loadMore={loadMore}
        setSelectedAsset={setSelectedAsset}
      />
      <ul
        ref={fakeGridRef}
        className="fake-grid tw-grid tw-grid-cols-[repeat(auto-fill,minmax(260px,1fr))] tw-gap-2 tw-overflow-x-auto"
      />
    </>
  )
}

export default AssetsVirtualGrid
